/**
 * Types and constants for MediaLibraryPanel components (Free Media Library)
 */

import { Image, Music, Video } from "lucide-react";

import type { PaginatedListResponse } from "@/types/pagination.types";

// Media types
type MediaType = "image" | "video" | "audio";

// Media categories
type MediaCategory =
  | "all"
  | "photos"
  | "illustrations"
  | "graphics"
  | "vectors"
  | "icons";

// Media providers
type MediaProvider = "pexels" | "unsplash" | "pixabay" | "freesound";

// Orientation options
type MediaOrientation = "landscape" | "portrait" | "square" | "squarish";

// Sort order
type MediaSortOrder = "popular" | "latest";

// Video size filter (Pexels only)
type VideoSize = "large" | "medium" | "small";

// Video type filter (Pixabay only)
type VideoType = "all" | "film" | "animation";

// Color filter options
type MediaColor =
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink"
  | "brown"
  | "black"
  | "white"
  | "gray"
  | "teal"
  | "black_and_white";

// Constants
const MEDIA_TYPES: Array<{
  value: MediaType;
  label: string;
  icon: any;
}> = [
  { value: "image", label: "Images", icon: Image },
  { value: "video", label: "Videos", icon: Video },
  { value: "audio", label: "Sounds", icon: Music },
];

// Provider mappings - search all providers for each media type
const MEDIA_TYPE_TO_PROVIDER: Record<MediaType, MediaProvider | "all"> = {
  image: "all", // Search Unsplash + Pexels + Pixabay for images
  video: "all", // Search Pexels + Pixabay for videos
  audio: "all", // Search Freesound for audio
};

const MEDIA_PROVIDERS: Array<{
  value: MediaProvider | "all";
  label: string;
  supportedTypes: MediaType[];
}> = [
  {
    value: "all",
    label: "All Providers",
    supportedTypes: ["image", "video", "audio"],
  },
  { value: "unsplash", label: "Unsplash", supportedTypes: ["image"] },
  { value: "pexels", label: "Pexels", supportedTypes: ["image", "video"] },
  {
    value: "pixabay",
    label: "Pixabay",
    supportedTypes: ["image", "video"],
  },
  {
    value: "freesound",
    label: "Freesound",
    supportedTypes: ["audio"],
  },
];

const ORIENTATION_OPTIONS: Array<{
  value: MediaOrientation | "any";
  label: string;
}> = [
  { value: "any", label: "Any Orientation" },
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" },
  { value: "square", label: "Square" },
];

const COLOR_OPTIONS: Array<{
  value: MediaColor | "any";
  label: string;
  hex: string;
}> = [
  { value: "any", label: "Any Color", hex: "" },
  { value: "red", label: "Red", hex: "#EF4444" },
  { value: "orange", label: "Orange", hex: "#F97316" },
  { value: "yellow", label: "Yellow", hex: "#EAB308" },
  { value: "green", label: "Green", hex: "#22C55E" },
  { value: "teal", label: "Teal", hex: "#14B8A6" },
  { value: "blue", label: "Blue", hex: "#3B82F6" },
  { value: "purple", label: "Purple", hex: "#A855F7" },
  { value: "pink", label: "Pink", hex: "#EC4899" },
  { value: "brown", label: "Brown", hex: "#92400E" },
  { value: "black", label: "Black", hex: "#171717" },
  { value: "gray", label: "Gray", hex: "#9CA3AF" },
  { value: "white", label: "White", hex: "#F5F5F5" },
  { value: "black_and_white", label: "B&W", hex: "" },
];

const SORT_OPTIONS: Array<{
  value: MediaSortOrder;
  label: string;
}> = [
  { value: "popular", label: "Popular" },
  { value: "latest", label: "Latest" },
];

const VIDEO_SIZE_OPTIONS: Array<{
  value: VideoSize | "any";
  label: string;
}> = [
  { value: "any", label: "Any Quality" },
  { value: "large", label: "4K" },
  { value: "medium", label: "Full HD" },
  { value: "small", label: "HD" },
];

const VIDEO_TYPE_OPTIONS: Array<{
  value: VideoType | "all";
  label: string;
}> = [
  { value: "all", label: "All Types" },
  { value: "film", label: "Film" },
  { value: "animation", label: "Animation" },
];

// Search result item from API
interface MediaLibraryResult {
  id: string;
  provider: MediaProvider;
  type: MediaType;
  title: string;
  thumbnailUrl: string;
  previewUrl: string;
  downloadUrl: string;
  width?: number;
  height?: number;
  duration?: number; // seconds for video/audio
  author: string;
  authorUrl: string;
  license: string;
  tags?: string[];
}

// Downloaded asset stored locally
interface LocalMediaAsset {
  id: string; // local-{filename}
  name: string;
  path: string;
  assetPath: string; // Relative path for src attributes
  size: number;
  mimeType: string;
  type: MediaType;
  createdAt: string;
  modifiedAt: string;
  previewUrl?: string; // Full URL for preview
}

// Downloading item (in progress)
interface DownloadingMedia {
  id: string; // requestId
  provider: MediaProvider;
  mediaId: string;
  type: MediaType;
  filename: string;
  thumbnailUrl?: string;
  status: "downloading" | "completed" | "failed";
  error?: string;
}

// Panel props
interface MediaLibraryPanelProps {
  devContainerId?: string;
  previewUrl?: string;
  onBack: () => void;
}

// API response type for media search
type MediaLibrarySearchResponse = PaginatedListResponse<MediaLibraryResult>;

interface MediaDownloadResponse {
  success: boolean;
  localFilePath?: string;
  fileSize?: number;
  error?: string;
}

interface ListMediaAssetsResponse {
  success: boolean;
  files?: Array<{
    name: string;
    path: string;
    assetPath: string;
    size: number;
    mimeType: string;
    type: MediaType;
    createdAt: string;
    modifiedAt: string;
  }>;
  error?: string;
}

export type {
  MediaType,
  MediaCategory,
  MediaProvider,
  MediaOrientation,
  MediaColor,
  MediaSortOrder,
  VideoSize,
  VideoType,
  MediaLibraryResult,
  LocalMediaAsset,
  DownloadingMedia,
  MediaLibraryPanelProps,
  MediaLibrarySearchResponse,
  MediaDownloadResponse,
  ListMediaAssetsResponse,
};

export {
  MEDIA_TYPES,
  MEDIA_TYPE_TO_PROVIDER,
  MEDIA_PROVIDERS,
  ORIENTATION_OPTIONS,
  COLOR_OPTIONS,
  SORT_OPTIONS,
  VIDEO_SIZE_OPTIONS,
  VIDEO_TYPE_OPTIONS,
};
